"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";

export default function WebsiteDevicesScroll() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-white overflow-hidden">
      <div className="max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 max-w-[760px] mx-auto"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#D7263D" }}>
            {t("Every screen", "Cada pantalla")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02] text-redy-charcoal">
            {t("Mobile first. ", "Mobile primero. ")}
            <span className="text-redy-charcoal/30">{t("Everywhere ready.", "Listo en todos lados.")}</span>
          </h2>
          <p className="text-[16px] font-medium text-redy-secondary leading-relaxed mt-6">
            {t(
              "Most people look you up on their phone. The site is built mobile-first and scales to tablet and desktop without breaking.",
              "La mayoría te busca desde el celular. El sitio es mobile-first y escala a tablet y desktop sin romperse."
            )}
          </p>
        </motion.div>

        {/* 3 devices side-by-side */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="flex items-end justify-center gap-4 md:gap-8 max-w-[1080px] mx-auto"
        >
          {/* Phone */}
          <DeviceFrame width={140} height={290} radius={24} bezel={5}>
            <SiteContent />
          </DeviceFrame>
          {/* Tablet */}
          <DeviceFrame width={210} height={290} radius={20} bezel={6} className="hidden md:block">
            <SiteContent />
          </DeviceFrame>
          {/* Desktop */}
          <div className="hidden lg:block">
            <div className="relative">
              <div className="bg-[#0a0a0a] rounded-t-[12px] p-[8px]">
                <div className="bg-white rounded-[6px] h-[210px] overflow-hidden relative">
                  {/* Browser chrome */}
                  <div className="h-5 bg-[#FAFAF8] border-b border-black/5 flex items-center px-2 gap-1">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#FF5F57]" />
                    <div className="w-1.5 h-1.5 rounded-full bg-[#FEBC2E]" />
                    <div className="w-1.5 h-1.5 rounded-full bg-[#28C840]" />
                  </div>
                  <div className="h-[calc(100%-20px)]">
                    <SiteContent />
                  </div>
                </div>
              </div>
              {/* Desktop stand */}
              <div className="h-3 bg-[#0a0a0a] mx-auto" style={{ width: 60 }} />
              <div className="h-1.5 bg-[#0a0a0a] rounded-b-[4px] mx-auto" style={{ width: 140 }} />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-12 flex flex-wrap justify-center gap-5"
        >
          {[
            { label: t("Built for speed", "Hecho para ser rápido"), value: t("Yes", "Sí") },
            { label: t("Mobile-first design", "Diseño mobile-first"), value: t("Yes", "Sí") },
            { label: t("Tablet + desktop", "Tablet + desktop"), value: t("Yes", "Sí") },
          ].map((s) => (
            <div key={s.label} className="text-center">
              <div className="text-[9px] font-bold uppercase tracking-widest text-redy-secondary/60 mb-1">
                {s.label}
              </div>
              <div className="text-[18px] font-black text-redy-charcoal tabular-nums">{s.value}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function DeviceFrame({
  children,
  width,
  height,
  radius,
  bezel,
  className = "",
}: {
  children: React.ReactNode;
  width: number;
  height: number;
  radius: number;
  bezel: number;
  className?: string;
}) {
  return (
    <div
      className={`bg-[#0a0a0a] shadow-[0_30px_80px_rgba(0,0,0,0.18)] ${className}`}
      style={{ width, height, borderRadius: radius, padding: bezel }}
    >
      <div
        className="w-full h-full overflow-hidden bg-white relative"
        style={{ borderRadius: radius - 4 }}
      >
        {children}
      </div>
    </div>
  );
}

function SiteContent() {
  const { t } = useLang();
  return (
    <div className="h-full flex flex-col">
      {/* Hero gradient */}
      <div
        className="h-[35%] relative overflow-hidden flex items-end p-2"
        style={{ background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)" }}
      >
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "radial-gradient(circle at 25% 35%, rgba(255,255,255,0.4) 0%, transparent 50%)",
          }}
        />
        <div className="relative">
          <div className="text-[6px] font-bold uppercase tracking-widest text-white/80">Dallas, TX</div>
          <div className="text-[10px] font-black text-white leading-none">Pepitos</div>
        </div>
      </div>
      {/* Menu list */}
      <div className="flex-1 px-1.5 py-1.5 space-y-1 bg-white">
        {["Birria", "Al Pastor", "Carnitas"].map((n, i) => (
          <div key={i} className="bg-[#FAFAF8] rounded p-1 flex justify-between items-center">
            <span className="text-[5px] font-bold text-redy-charcoal">{n}</span>
            <span className="text-[5px] text-redy-secondary tabular-nums">$4.50</span>
          </div>
        ))}
      </div>
      {/* CTA */}
      <div
        className="px-1.5 py-1.5 text-center text-[6px] font-bold text-white"
        style={{ background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)" }}
      >
        Order pickup
      </div>
    </div>
  );
}
